import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Pencil, Trash2, Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { title: "", desc: "", img: "" };
const inputCls =
  "w-full border border-white/15 bg-[#050505] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#0055FF]";

export default function GalleryManager({ token, onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/gallery`);
      setItems(r.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleErr = (e, fallback) => {
    if (e.response?.status === 401) onUnauthorized?.();
    toast.error(e.response?.data?.detail || fallback);
  };

  const save = async () => {
    try {
      if (form.id) await axios.put(`${API}/admin/gallery/${form.id}`, form, { headers });
      else await axios.post(`${API}/admin/gallery`, form, { headers });
      toast.success(form.id ? "Photo updated" : "Photo added to gallery");
      setForm(null);
      load();
    } catch (e) {
      handleErr(e, "Failed to save photo");
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Remove "${g.title || "this photo"}" from the gallery?`)) return;
    try {
      await axios.delete(`${API}/admin/gallery/${g.id}`, { headers });
      toast.success("Photo removed");
      load();
    } catch (e) {
      handleErr(e, "Failed to delete photo");
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/upload-image`, fd, { headers });
      setForm((f) => ({ ...f, img: `/api/uploads/${r.data.path}` }));
      toast.success("Photo uploaded");
    } catch (e) {
      handleErr(e, "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="gallery-manager" className="mt-8">
      <div className="flex items-center justify-between gap-4">
        <p className="max-w-2xl text-sm leading-relaxed text-[#A3A3A3]">
          Add unlimited photos to the Project Gallery on your website — each with a subtitle and description. Upload
          from your phone or computer.
        </p>
        <button
          data-testid="gallery-add-button"
          onClick={() => setForm({ ...EMPTY })}
          className="flex shrink-0 items-center gap-2 bg-[#0055FF] px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest transition-colors hover:bg-[#3377FF]"
        >
          <Plus size={14} /> Add Photo
        </button>
      </div>

      {form && (
        <div data-testid="gallery-form" className="mt-6 border border-[#0055FF]/40 bg-[#0A0A0A] p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold">{form.id ? "Edit Photo" : "New Gallery Photo"}</h3>
            <button data-testid="gallery-form-cancel-button" onClick={() => setForm(null)} aria-label="Close form">
              <X size={18} className="text-white/60 hover:text-white" />
            </button>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              data-testid="gallery-form-title-input"
              placeholder="Subtitle / title (e.g. Warehouse Roofing — Patancheru)"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={inputCls}
            />
            <div className="flex gap-2">
              <input
                data-testid="gallery-form-img-input"
                placeholder="Image URL (https:// or uploaded) *"
                value={form.img}
                onChange={(e) => setForm({ ...form, img: e.target.value })}
                className={inputCls}
              />
              <label
                data-testid="gallery-form-upload"
                className={`flex shrink-0 cursor-pointer items-center gap-1.5 bg-white px-4 text-[11px] font-bold uppercase tracking-widest text-[#050505] hover:bg-white/80 ${
                  uploading ? "pointer-events-none opacity-50" : ""
                }`}
              >
                <Upload size={12} /> {uploading ? "..." : "Upload"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    upload(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
            <textarea
              data-testid="gallery-form-desc-input"
              placeholder="Description (optional)"
              rows={3}
              value={form.desc}
              onChange={(e) => setForm({ ...form, desc: e.target.value })}
              className={`${inputCls} resize-none sm:col-span-2`}
            />
          </div>
          {form.img && (
            <img src={form.img} alt="preview" className="mt-4 max-h-48 border border-white/10 object-cover" />
          )}
          <button
            data-testid="gallery-form-save-button"
            onClick={save}
            disabled={!form.img.trim()}
            className="mt-5 bg-[#0055FF] px-8 py-3 font-display text-sm font-bold uppercase tracking-widest transition-colors hover:bg-[#3377FF] disabled:opacity-40"
          >
            {form.id ? "Save Changes" : "Add to Gallery"}
          </button>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-list">
        {items.length === 0 && (
          <p className="border border-white/10 bg-[#0A0A0A] p-8 text-center text-sm text-[#A3A3A3] sm:col-span-2 lg:col-span-3">
            No gallery photos yet. Click "Add Photo" — the gallery section appears on your website as soon as you add
            the first one.
          </p>
        )}
        {items.map((g) => (
          <div key={g.id} data-testid={`gallery-row-${g.id}`} className="border border-white/10 bg-[#0A0A0A] p-3">
            <img src={g.img} alt={g.title || "Gallery photo"} className="aspect-[4/3] w-full object-cover" />
            <p className="mt-2 truncate text-sm font-semibold">{g.title || "(no subtitle)"}</p>
            {g.desc && <p className="mt-0.5 line-clamp-1 text-xs text-[#A3A3A3]">{g.desc}</p>}
            <div className="mt-3 flex gap-2">
              <button
                data-testid={`gallery-edit-${g.id}`}
                onClick={() => setForm({ ...g })}
                className="flex flex-1 items-center justify-center gap-1.5 border border-white/15 px-3 py-2 text-[11px] font-bold uppercase tracking-widest hover:border-[#0055FF]"
              >
                <Pencil size={12} /> Edit
              </button>
              <button
                data-testid={`gallery-delete-${g.id}`}
                onClick={() => remove(g)}
                className="flex flex-1 items-center justify-center gap-1.5 border border-red-500/40 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-red-400 hover:bg-red-500/10"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
